import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BrainCircuit, Compass, Scale, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";

const FILTERS = [
  "All Insights",
  "Country Comparisons",
  "Retirement Abroad",
  "Cost & Budget",
  "Healthcare",
  "Residency & Visas",
  "Lifestyle & Daily Life",
];

const INSIGHTS = [
  {
    id: 1,
    title: "Why Reading About Countries Isn't the Same as Choosing One",
    href: "/insights/why-reading-about-countries-isnt-the-same-as-choosing-one",
    category: "Decision Framework",
    tags: ["Lifestyle & Daily Life", "Residency & Visas"],
    summary: "Research can tell you what a country is like. It cannot tell you whether that country actually fits the life you want to build.",
    image: "https://images.unsplash.com/photo-1609072730603-1d321d86d8ff?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    badge: "START HERE",
    readingTime: "4 min read",
  },
  {
    id: 2,
    title: "Why cost-to-retire-abroad charts can mislead",
    href: "/insights/why-cost-to-retire-abroad-charts-are-misleading",
    category: "Retirement Abroad",
    tags: ["Retirement Abroad", "Cost & Budget"],
    summary: "Cost charts flatten the decision too early. The real question is what daily life costs in the version of the move you would actually sustain.",
    image: "https://images.unsplash.com/photo-1707157284454-553ef0a4ed0d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    readingTime: "3 min read",
  },
  {
    id: 3,
    title: "Portugal vs Spain: Which one actually fits your priorities?",
    href: "/insights/portugal-vs-spain-living-abroad",
    category: "Country Comparisons",
    tags: ["Country Comparisons", "Lifestyle & Daily Life"],
    summary: "Both countries stay in the conversation for good reasons. The better fit usually depends on which tradeoffs matter more once daily life becomes the focus.",
    image: "https://images.unsplash.com/photo-1499590206382-9f85678c0e3e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    readingTime: "4 min read",
  },
  {
    id: 4,
    title: "The Biggest Mistake People Make When Choosing a Country",
    href: "/insights/the-biggest-mistake-people-make-when-choosing-a-country",
    category: "Decision Framework",
    tags: ["Lifestyle & Daily Life", "Residency & Visas"],
    summary: "The early mistake is not researching too little. It is comparing countries before you know which priorities should do the filtering.",
    image: "https://images.unsplash.com/photo-1516546453174-5e1098a4b4af?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    badge: "NEW INSIGHT",
    readingTime: "2 min read",
  },
  {
    id: 5,
    title: "How to create a country shortlist for moving abroad",
    href: "/insights/create-country-shortlist-for-moving-abroad",
    category: "Decision Framework",
    tags: ["Residency & Visas"],
    summary: "A useful shortlist narrows the field before the research spirals. It keeps the decision grounded in fit, realism, and the tradeoffs you can actually live with.",
    image: "https://images.unsplash.com/photo-1620275765334-4ed948bb4502?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    readingTime: "4 min read",
  },
  {
    id: 6,
    title: "What Country Should I Move To?",
    href: "/insights/what-country-should-i-move-to",
    category: "Decision Framework",
    tags: ["Residency & Visas"],
    summary: "The decision gets clearer when you stop chasing the best country overall and start weighing budget, healthcare, residency practicality, and daily life together.",
    image: "https://images.unsplash.com/photo-1531346878377-a5be20888e57?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    readingTime: "4 min read",
  },
  {
    id: 7,
    title: "Best Countries for Americans to Move Abroad",
    href: "/insights/best-countries-for-americans-to-move-abroad",
    category: "Country Comparisons",
    tags: ["Country Comparisons", "Residency & Visas"],
    summary: "The strongest options for Americans usually separate on practical stay paths, healthcare confidence, affordability, and how easy daily life feels once the move is real.",
    image: "https://images.unsplash.com/photo-1680728398778-af1bc2aded64?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    readingTime: "3 min read",
  },
  {
    id: 8,
    title: "Best Countries for Remote Workers and Freelancers",
    href: "/insights/best-countries-for-remote-workers-and-freelancers",
    category: "Lifestyle & Daily Life",
    tags: ["Lifestyle & Daily Life", "Residency & Visas"],
    summary: "Remote workers and freelancers often need a different country filter. The better answer depends on infrastructure, visa flexibility, cost of living, time zones, and the pace that supports how you actually work.",
    image: "https://images.unsplash.com/photo-1617519801130-937a5f6eb62a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    readingTime: "3 min read",
  },
  {
    id: 9,
    title: "How Much Money Do You Need to Live Abroad?",
    href: "/insights/how-much-money-do-you-need-to-live-abroad",
    category: "Cost & Budget",
    tags: ["Cost & Budget"],
    summary: "The workable budget is rarely just a national average. Housing pressure, city choice, healthcare, and pace of life change what realistic monthly costs actually look like.",
    image: "https://images.unsplash.com/photo-1543286386-713bdd548da4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    readingTime: "3 min read",
  },
  {
    id: 10,
    title: "Countries With the Best Healthcare for Expats",
    href: "/insights/countries-with-the-best-healthcare-for-expats",
    category: "Healthcare",
    tags: ["Healthcare"],
    summary: "Healthcare quality matters differently depending on age, peace-of-mind needs, and how much system variability you are willing to accept in day-to-day life abroad.",
    image: "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    readingTime: "3 min read",
  },
  {
    id: 11,
    title: "Portugal vs Spain for American Retirees",
    href: "/insights/portugal-vs-spain-for-american-retirees",
    category: "Retirement Abroad",
    tags: ["Retirement Abroad", "Country Comparisons"],
    summary: "Retirement decisions between Portugal and Spain usually tighten around affordability, healthcare confidence, residency practicality, and the day-to-day pace you want to sustain.",
    image: "https://images.unsplash.com/photo-1499590206382-9f85678c0e3e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    readingTime: "3 min read",
  },
];

const CTA_POINTS = [
  {
    icon: Compass,
    title: "Start with the decision, not the hype",
    copy: "Use the insights to understand what should shape the choice before you let country lists or trends take over.",
  },
  {
    icon: Scale,
    title: "Learn the tradeoffs behind the answer",
    copy: "The goal is not to name one universally best country. It is to show why different priorities lead to different conclusions.",
  },
  {
    icon: ShieldCheck,
    title: "Independent, practical, and citable",
    copy: "Each article is meant to answer a real relocation question clearly enough to stand on its own and useful enough to build on.",
  },
];

export default function InsightsHub() {
  const [activeFilter, setActiveFilter] = useState("All Insights");

  const visibleInsights = useMemo(() => {
    if (activeFilter === "All Insights") return INSIGHTS;
    return INSIGHTS.filter((insight) => insight.tags.includes(activeFilter));
  }, [activeFilter]);

  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="insights-hub-page">
      <Header />

      <main>
        <section className="border-b border-stone-200/80 bg-[#f1ede6] px-4 py-10 sm:px-6 lg:px-8 lg:py-14" data-testid="insights-hero-section">
          <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.45fr_0.85fr] lg:items-stretch">
            <div className="relative overflow-hidden rounded-[2.2rem] border border-stone-300/70 bg-stone-200 shadow-[0_20px_60px_rgba(28,25,23,0.10)]" data-testid="insights-hero-main-panel">
              <img
                src="https://images.unsplash.com/photo-1747328085826-998aaf4df365?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Coastal town and sea representing relocation analysis across countries"
                className="absolute inset-0 h-full w-full object-cover object-center"
                data-testid="insights-hero-image"
              />
              <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(20,17,15,0.72)_0%,rgba(20,17,15,0.48)_35%,rgba(20,17,15,0.18)_100%)]" />
              <div className="relative flex min-h-[420px] flex-col justify-end p-7 sm:p-9 lg:min-h-[470px] lg:p-11" data-testid="insights-hero-copy-block">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-200/90" data-testid="insights-hero-kicker">Insights hub</p>
                <h1 className="mt-4 max-w-[12ch] font-display text-4xl tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl" data-testid="insights-hero-title">
                  Relocation Insights
                </h1>
                <p className="mt-5 max-w-2xl text-base leading-8 text-stone-100 sm:text-lg" data-testid="insights-hero-subheadline">
                  Independent guidance for people trying to choose where to live abroad.
                </p>
                <p className="mt-3 max-w-2xl text-base leading-8 text-stone-200/95" data-testid="insights-hero-body">
                  These articles are designed to answer the questions people actually ask, explain the tradeoffs honestly, and help readers think more clearly rather than chase generic rankings.
                </p>
                <div className="mt-7">
                  <Button asChild className="h-12 rounded-full bg-white px-6 text-sm font-medium text-stone-900 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-amber-100" data-testid="insights-hero-primary-cta">
                    <Link to="/auth">
                      Take the Assessment
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            <aside className="rounded-[2rem] border border-stone-300/80 bg-white/90 p-7 shadow-[0_18px_48px_rgba(28,25,23,0.07)] sm:p-8" data-testid="insights-hero-side-card">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 text-stone-900" data-testid="insights-hero-side-icon">
                <BrainCircuit className="h-5 w-5" />
              </div>
              <h2 className="mt-6 font-manrope text-2xl text-stone-900" data-testid="insights-hero-side-title">
                Built for people who want clarity before they commit.
              </h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="insights-hero-side-copy">
                <p>No sponsored destinations. No absolute rankings presented as truth.</p>
                <p>Just practical explanations of what usually matters, why people reach different answers, and how to judge those tradeoffs for your own move.</p>
              </div>
            </aside>
          </div>
        </section>

        <section className="px-4 pt-10 sm:px-6 lg:px-8 lg:pt-12" data-testid="insights-key-takeaway-section">
          <div className="mx-auto max-w-7xl rounded-[2rem] border border-emerald-200/80 bg-[linear-gradient(135deg,rgba(236,244,239,0.94)_0%,rgba(247,250,248,0.96)_100%)] px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10">
            <div className="mb-4 h-1.5 w-14 rounded-full bg-emerald-500/70" data-testid="insights-key-takeaway-accent" />
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="insights-key-takeaway-label">Key Takeaway</p>
            <div className="mt-4 max-w-4xl space-y-4 text-base leading-8 text-stone-700" data-testid="insights-key-takeaway-copy">
              <p>Relocation Clarity Insights exists to help readers understand how to choose a country, not simply which country is popular at the moment.</p>
              <p>The articles are built to answer important relocation questions clearly, explain why conclusions differ across people and priorities, and give readers language they can trust, quote, and build on as they narrow the decision.</p>
            </div>
          </div>
        </section>

        <section className="px-4 pt-10 sm:px-6 lg:px-8 lg:pt-12" data-testid="insights-guided-path-section">
          <div className="mx-auto max-w-7xl">
            <ContextualLinkPanel
              kicker="Where to start"
              title="If you’re early in the decision, this is the clearest path through the insights."
              description="These pages are arranged to move from the broad relocation question into a tighter shortlist, then into a real comparison, then into a preview of the Blueprint before you decide whether to personalize the process. Each one is meant to teach you something self-contained along the way, not just push you to the next click."
              testIdPrefix="insights-guided-path"
              gridClassName="md:grid-cols-2 xl:grid-cols-4"
              items={[
                {
                  slug: "broad-question",
                  eyebrow: "Step 1",
                  title: "Start with the big question",
                  description: "Understand what actually determines country fit before you chase country lists.",
                  ctaLabel: "Read the decision guide",
                  to: "/insights/what-country-should-i-move-to",
                },
                {
                  slug: "shortlist-guide",
                  eyebrow: "Step 2",
                  title: "Narrow the shortlist",
                  description: "Move from broad country interest into a smaller set of options worth comparing seriously.",
                  ctaLabel: "Build the shortlist",
                  to: "/insights/create-country-shortlist-for-moving-abroad",
                },
                {
                  slug: "country-comparison",
                  eyebrow: "Step 3",
                  title: "See a real comparison",
                  description: "Watch how a practical head-to-head comparison clarifies the tradeoffs between two realistic options.",
                  ctaLabel: "Open the comparison",
                  to: "/insights/portugal-vs-spain-living-abroad",
                },
                {
                  slug: "sample-blueprint",
                  eyebrow: "Step 4",
                  title: "Preview the Blueprint",
                  description: "See how the paid experience organizes the shortlist, tradeoffs, and next steps before you commit.",
                  ctaLabel: "Preview the sample",
                  to: "/sample-plan",
                },
              ]}
            />
          </div>
        </section>

        <section className="px-4 py-10 sm:px-6 lg:px-8 lg:py-12" data-testid="insights-filter-section">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-wrap gap-3" data-testid="insights-filter-tabs">
              {FILTERS.map((filter) => {
                const isActive = activeFilter === filter;
                return (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setActiveFilter(filter)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 ${isActive ? "border-stone-900 bg-stone-900 text-stone-50" : "border-stone-300 bg-white/90 text-stone-700 hover:border-stone-500 hover:text-stone-900"}`}
                    data-testid={`insights-filter-tab-${filter.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {filter}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-4 pb-16 sm:px-6 lg:px-8 lg:pb-20" data-testid="insights-grid-section">
          <div className="mx-auto max-w-7xl">
            {visibleInsights.length ? (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="insights-card-grid">
                {visibleInsights.map((insight) => (
                  <article key={insight.id} className="group overflow-hidden rounded-[2rem] border border-stone-300/80 bg-white/92 shadow-[0_18px_48px_rgba(28,25,23,0.06)] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_24px_58px_rgba(28,25,23,0.09)]" data-testid={`insights-card-${insight.id}`}>
                    <Link to={insight.href} className="block" data-testid={`insights-card-link-${insight.id}`}>
                      <div className="aspect-[1.22] overflow-hidden" data-testid={`insights-card-image-wrapper-${insight.id}`}>
                        <img
                          src={insight.image}
                          alt={insight.title}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                          data-testid={`insights-card-image-${insight.id}`}
                        />
                      </div>
                      <div className="p-6 sm:p-7">
                        <div className="flex flex-wrap items-center gap-3" data-testid={`insights-card-meta-row-${insight.id}`}>
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid={`insights-card-category-${insight.id}`}>
                            {insight.category}
                          </p>
                          {insight.badge ? (
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${insight.badge === "START HERE" ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}
                              data-testid={`insights-card-badge-${insight.id}`}
                            >
                              {insight.badge}
                            </span>
                          ) : null}
                        </div>
                        <h2 className="mt-3 font-manrope text-2xl leading-tight text-stone-900" data-testid={`insights-card-title-${insight.id}`}>
                          {insight.title}
                        </h2>
                        <p className="mt-4 text-base leading-8 text-stone-600" data-testid={`insights-card-summary-${insight.id}`}>
                          {insight.summary}
                        </p>
                        {insight.readingTime ? (
                          <p className="mt-5 text-sm text-stone-500" data-testid={`insights-card-reading-time-${insight.id}`}>
                            {insight.readingTime}
                          </p>
                        ) : null}
                        <span className="mt-6 inline-flex items-center text-sm font-medium text-stone-800 transition-colors duration-200 group-hover:text-stone-950" data-testid={`insights-card-cta-${insight.id}`}>
                          Read Analysis
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 text-center shadow-[0_18px_48px_rgba(28,25,23,0.05)]" data-testid="insights-empty-state">
                <h2 className="font-manrope text-2xl text-stone-900" data-testid="insights-empty-state-title">More analysis is on the way.</h2>
                <p className="mt-4 text-base leading-8 text-stone-600" data-testid="insights-empty-state-copy">
                  Explore the broader insights first, or take the assessment to narrow the shortlist around your move.
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24" data-testid="insights-bottom-cta-section">
          <div className="mx-auto max-w-7xl rounded-[2.2rem] border border-emerald-200/70 bg-[linear-gradient(135deg,rgba(221,236,230,0.95)_0%,rgba(233,243,239,0.96)_100%)] px-7 py-10 shadow-[0_20px_54px_rgba(28,25,23,0.07)] sm:px-10 lg:px-12 lg:py-12">
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="insights-bottom-cta-kicker">Personalized next step</p>
                <h2 className="mt-3 font-manrope text-3xl text-stone-900 sm:text-4xl" data-testid="insights-bottom-cta-title">
                  Go beyond the insights. Get your personalized shortlist.
                </h2>
                <p className="mt-4 max-w-3xl text-base leading-8 text-stone-700" data-testid="insights-bottom-cta-copy">
                  The assessment compares your priorities, budget, and relocation goals against real-world country factors.
                </p>
              </div>
              <div className="flex lg:justify-end">
                <Button asChild className="h-12 rounded-full bg-stone-950 px-6 text-sm font-medium text-stone-50 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-black" data-testid="insights-bottom-cta-button">
                  <Link to="/auth">
                    Take the Assessment
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-3" data-testid="insights-bottom-cta-points-grid">
              {CTA_POINTS.map((point, index) => {
                const Icon = point.icon;
                return (
                  <div key={point.title} className="rounded-[1.5rem] border border-white/70 bg-white/70 p-5" data-testid={`insights-bottom-cta-point-${index + 1}`}>
                    <Icon className="h-5 w-5 text-stone-700" />
                    <h3 className="mt-4 font-manrope text-lg text-stone-900" data-testid={`insights-bottom-cta-point-title-${index + 1}`}>{point.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-stone-600" data-testid={`insights-bottom-cta-point-copy-${index + 1}`}>{point.copy}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}